import { t } from 'i18next';
import { Severity } from '../../../common/enums/Severity';

// @ts-expect-error "Named capturing groups are only available when targeting 'ES2018' or later"
export const SCRIPT_REGEX = /<script.*?src="(?<script>.+?)".*?>/s;

type PrefilledFields = {
  fullname?: string;
  severity?: Severity;
  summary?: string;
  description?: string;
};

const ID_JIRA_ISSUE_COLLECTOR = 'jira-issue-collector-script';

// Timeout to download the main collector script
const SCRIPT_TIMEOUT_MS = 20_000;

// Poll intervals for the rendering of the collector frame
const LOAD_POLL_MS = 1_000;
const LOAD_POLL_RETRY = 20;

const getCollectorElements = () => {
  return [
    document.getElementById(ID_JIRA_ISSUE_COLLECTOR),
    document.getElementById('atlwdg-container'),
    document.getElementById('atlwdg-blanket'),
    document.getElementById('atlwdg-loading'),
  ].filter((e) => e !== null);
};

const isCollectorDisplayed = () => {
  const container = document.getElementById('atlwdg-container');
  return container && !container.classList.contains('atlwdg-hidden');
};

const severityToPriority = (severity: Severity | undefined) => {
  switch (severity) {
    case Severity.PASSED:
      return '5';
    case Severity.LOW:
      return '4';
    case Severity.MED:
      return '3';
    case Severity.HIGH:
      return '2';
    default:
      return '3';
  }
};

export class JiraUtils {
  private constructor() {}

  static loadIssueCollectorScript(issueCollectorCode: string, fields: PrefilledFields = {}) {
    return new Promise<void>((resolve, reject) => {
      if (getCollectorElements().length > 0) {
        // Reset any previously loaded script, configuration might have changed
        // without a full page reload
        JiraUtils.unloadIssueCollectorScript();
      }

      const scriptUrl = JiraUtils.extractScriptUrlFromCode(issueCollectorCode);
      if (!scriptUrl) {
        JiraUtils.unloadIssueCollectorScript();
        return reject(new Error(t('jiraIssueCollector.errors.invalid')));
      }

      const timeoutId = setTimeout(() => {
        JiraUtils.unloadIssueCollectorScript();
        reject(new Error(t('jiraIssueCollector.errors.timeout')));
      }, SCRIPT_TIMEOUT_MS);

      window.ATL_JQ_PAGE_PROPS = {
        triggerFunction: function (showCollectorDialog: () => void) {
          window.showIssueCollector = () => {
            return new Promise<void>((success, error) => {
              showCollectorDialog();
              let retryCount = 0;
              const interval = setInterval(() => {
                if (isCollectorDisplayed()) {
                  clearInterval(interval);
                  return success();
                }
                retryCount++;
                if (retryCount === LOAD_POLL_RETRY) {
                  clearInterval(interval);
                  JiraUtils.unloadIssueCollectorScript();
                  return error(new Error(t('jiraIssueCollector.errors.timeout')));
                }
              }, LOAD_POLL_MS);
            });
          };
          clearTimeout(timeoutId);
          resolve();
        },
        fieldValues: {
          ...fields,
          priority: severityToPriority(fields.severity),
        },
      };

      const jiraScriptEl = document.createElement('script');
      jiraScriptEl.src = scriptUrl;
      jiraScriptEl.id = ID_JIRA_ISSUE_COLLECTOR;
      jiraScriptEl.addEventListener('error', () => {
        clearTimeout(timeoutId);
        JiraUtils.unloadIssueCollectorScript();
        reject(new Error(t('jiraIssueCollector.errors.generic')));
      });
      document.body.appendChild(jiraScriptEl);
    });
  }

  static unloadIssueCollectorScript() {
    const elements = getCollectorElements();
    elements.forEach((e) => e?.remove());
    window.ATL_JQ_PAGE_PROPS = null;
    window.showIssueCollector = null;
  }

  static extractScriptUrlFromCode(code: string) {
    // Validate max length of the overall input
    if (code.length > 1000) return null;

    const scriptFromHtml = code.match(SCRIPT_REGEX)?.[1];

    return scriptFromHtml ?? null;
  }
}
